import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { exportToExcel, exportMultipleToExcel } from '../utils/exportToExcel';
import { hotelsAPI, roomsAPI, travelomatixRoomsAPI, amenitiesAPI } from '../services/api';
import './Hotels.css';
import axios from 'axios'; // Add axios import for file uploads
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Import helper functions
import { 
  validateHotelForm,
  formatHotelDataForSubmission,
  initializeHotelFormDataForEdit
} from '../helpers/hotelHelpers';
import { 
  handleHotelSubmit as handleHotelSubmitHelper
} from '../helpers/submissionHelpers';
import { 
  handleEditHotel as handleEditHotelHelper,
  handleDeleteHotel as handleDeleteHotelHelper,
  handleConfirmDelete,
  handleCancelDelete,
  confirmDeleteHotel as confirmDeleteHotelHelper
} from '../helpers/hotelOperationHelpers';
import { 
  resetHotelForm as resetHotelFormHelper,
  handleHotelInputChange,
  handleRoomInputChange,
  handleTravelomatixRoomInputChange,
  handleRoomSearchChange as handleRoomSearchChangeHelper,
  handleHotelImageChange,
  handleRoomImageChange,
  resetRoomForm as resetRoomFormHelper,
  resetTravelomatixRoomForm,
  resetTravelomatixRoomFormAndEdit,
  resetRoomSearchForm as resetRoomSearchFormHelper
} from '../helpers/formHandlerHelpers';
import { 
  handleImageUrlChange,
  addImageUrl,
  removeImageUrl 
} from '../helpers/formHelpers';
import { 
  validateRoomForm,
  validateTravelomatixRoomForm,
  formatRoomDataForSubmission,
  initializeRoomFormDataForEdit,
  initializeRoomFormDataForAdd
} from '../helpers/roomHelpers';
import { 
  handleTravelomatixRoomSubmit,
  handleRoomSubmit as handleRoomSubmitHelper,
  handleEditRoom as handleEditRoomHelper,
  handleDeleteRoom as handleDeleteRoomHelper,
  handleRoomSearchSubmit as handleRoomSearchSubmitHelper,
  addRoomFromTravelomatix
} from '../helpers/roomOperationHelpers';
import { exportHotelsToExcel, exportRoomsToExcel, exportAllToExcel } from '../helpers/exportHelpers';
import { HotelSkeleton, toggleHotelDetails } from '../helpers/uiHelpers';
import { searchCities, searchHotels } from '../helpers/searchHelpers';
import { 
  debounce,
  handleSearchChange as handleSearchChangeHelper,
  handleSearchSubmit as handleSearchSubmitHelper,
  selectCityFromSearch,
  selectHotelFromSearch
} from '../helpers/searchHandlers';
import { 
  fetchHotels,
  fetchRoomsForHotel,
  fetchRoomsForExpandedHotels
} from '../helpers/dataFetchers';
import { 
  getPaginatedHotels,
  handlePageChange,
  getTotalPages
} from '../helpers/paginationHelpers';
import { 
  getRoomsForHotel,
  confirmDeleteHotel,
  validateRoomFormHelper,
  searchRoomsHelper
} from '../helpers/hotelHelpers';

// Add this at the top with other constants
const AMADEUS_API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/amadeus`;
const TRAVEL_API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/travelomatix`;

const Hotels = () => {
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState({});
  const [loading, setLoading] = useState({ hotels: false, rooms: {}, search: false });
  const [errors, setErrors] = useState({});
  const [formErrors, setFormErrors] = useState({ hotel: {}, room: {} });
  const [showHotelForm, setShowHotelForm] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [hotelFormData, setHotelFormData] = useState({
    name_en: '',
    featured: false,
    city: '',
    country: '',
    images_urls: [''], // Keep empty when adding, populate when editing
    supplier_id: '',
    commission_percentage: 10,
    // Add accessibility template IDs
    accessibility_template_ids: [],
    // Add amenities IDs
    amenities: []
  });
  const [roomFormData, setRoomFormData] = useState({
    hotel_id: '',
    room_type: '',
    name_en: '',
    description_en: '',
    price_per_night: '',
    capacity: 1,
    currency: 'USD',
    image_url: '',
    imageFile: null
  });
  
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  
  // State to track which hotel details are expanded
  const [expandedHotels, setExpandedHotels] = useState(new Set());
  
  // Add search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [showCitySearchResults, setShowCitySearchResults] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  // Add date state for search
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);

  // Add accessibility templates state
  const [accessibilityTemplates, setAccessibilityTemplates] = useState({});
  const [accessibilityLoading, setAccessibilityLoading] = useState(false);

  // Add amenities state
  const [amenities, setAmenities] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);

  // Add room search state variables
  const [roomSearchParams, setRoomSearchParams] = useState({
    resultToken: '',
    checkInDate: '',
    checkOutDate: '',
    guestNationality: 'SA',
    noOfRooms: 1,
    noOfNights: 1,
    adults: 2,
    children: 0,
    childAges: []
  });
  const [roomSearchResults, setRoomSearchResults] = useState([]);
  const [roomSearchLoading, setRoomSearchLoading] = useState(false);
  const [roomSearchError, setRoomSearchError] = useState(null);
  const [selectedHotelForRoomSearch, setSelectedHotelForRoomSearch] = useState(null);
  const [showRoomSearchResults, setShowRoomSearchResults] = useState(false);
  const [showRoomSearchModal, setShowRoomSearchModal] = useState(false);
  
  // Add new state for the Travelomatix room form
  const [showTravelomatixRoomForm, setShowTravelomatixRoomForm] = useState(false);
  const [travelomatixRoomData, setTravelomatixRoomData] = useState(null);
  // Add state for room form data
  const [travelomatixRoomFormData, setTravelomatixRoomFormData] = useState({
    hotel_id: '',
    name_en: '',
    name_ar: '',
    supplier_id: '',
    room_type: '',
    // description_en: '',
    // description_ar: '',
    capacity: 1,
    price_per_night: '',
    currency: 'USD',
    images_urls: [''],
    // Add accessibility template IDs
    accessibility_template_ids: []
  });
  const [travelomatixFormErrors, setTravelomatixFormErrors] = useState({});
  
  // Pagination state for hotels
  const [hotelPage, setHotelPage] = useState(1);
  const hotelsPerPage = 5;

  // Get paginated hotels
  const paginatedHotels = useMemo(() => {
    return getPaginatedHotels(hotels, hotelPage, hotelsPerPage);
  }, [hotels, hotelPage]);

  // Total pages for hotels
  const totalPages = getTotalPages(hotels, hotelsPerPage);

  // Handle page change
  // const handlePageChange = useCallback((newPage) => {
  //   setHotelPage(newPage);
  //   const hotelListElement = document.querySelector('.hotels-list');
  //   if (hotelListElement) {
  //     hotelListElement.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, []);

  // Fetch hotels from API
  useEffect(() => {
    fetchHotels(setLoading, setHotels, setErrors);
  }, []); // Only run on mount

  // Fetch accessibility templates
  useEffect(() => {
    const fetchAccessibilityTemplates = async () => {
      try {
        setAccessibilityLoading(true);
        const { accessibilityAPI } = await import('../services/api');
        const response = await accessibilityAPI.getAllTemplates();
        
        // Check if response data exists
        if (!response || !response.data || !response.data.data) {
          console.warn('No accessibility templates data received');
          setAccessibilityTemplates({});
          return;
        }
        
        const data = response.data.data;
        
        // Check if data is an object
        if (typeof data !== 'object' || data === null) {
          console.warn('Accessibility templates data is not an object');
          setAccessibilityTemplates({});
          return;
        }
        
        // Organize templates by category, filtering only features (not subcategories or feature categories)
        const organizedTemplates = {};
        
        // Process each category
        Object.keys(data).forEach(category => {
          const categoryData = data[category];
          
          // Skip if categoryData is not an object
          if (typeof categoryData !== 'object' || categoryData === null) {
            return;
          }
          
          const features = [];
          
          // Add standalone features
          if (Array.isArray(categoryData.features)) {
            features.push(...categoryData.features.map(item => ({
              ...item,
              category: category,
              type: 'feature',
              feature_category: null // Standalone features have no feature category
            })));
          }
          
          // Add features within feature categories
          if (Array.isArray(categoryData.feature_categories)) {
            categoryData.feature_categories.forEach(fc => {
              // Skip if fc is not an object or doesn't have features array
              if (typeof fc !== 'object' || fc === null || !Array.isArray(fc.features)) {
                return;
              }
              
              features.push(...fc.features.map(f => ({
                ...f,
                category: category,
                type: 'feature',
                feature_category: fc.name_en
              })));
            });
          }
          
          // Sort features: first by feature_category (alphabetically), then by name_en (alphabetically)
          features.sort((a, b) => {
            // First sort by feature_category (null values first)
            const categoryA = a.feature_category || '';
            const categoryB = b.feature_category || '';
            
            if (categoryA !== categoryB) {
              if (categoryA === '') return -1;
              if (categoryB === '') return 1;
              return categoryA.localeCompare(categoryB);
            }
            
            // Then sort by name_en
            return a.name_en.localeCompare(b.name_en);
          });
          
          // Only add category if it has features
          if (features.length > 0) {
            organizedTemplates[category] = features;
          }
        });
        
        setAccessibilityTemplates(organizedTemplates);
      } catch (error) {
        console.error('Error fetching accessibility templates:', error);
        setAccessibilityTemplates({}); // Set to empty object on error
      } finally {
        setAccessibilityLoading(false);
      }
    };
    
    fetchAccessibilityTemplates();
  }, []);

  // Fetch amenities
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        setAmenitiesLoading(true);
        const response = await amenitiesAPI.getAll();
        
        // Check if response data exists
        if (!response || !response.data) {
          console.warn('No amenities data received');
          setAmenities([]);
          return;
        }
        
        // Handle different possible response structures
        let amenitiesData = [];
        if (Array.isArray(response.data)) {
          amenitiesData = response.data;
        } else if (response.data.amenities && Array.isArray(response.data.amenities)) {
          amenitiesData = response.data.amenities;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          amenitiesData = response.data.data;
        }
        
        setAmenities(amenitiesData);
      } catch (error) {
        console.error('Error fetching amenities:', error);
        setAmenities([]); // Set to empty array on error
      } finally {
        setAmenitiesLoading(false);
      }
    };
    
    fetchAmenities();
  }, []);

  // Fetch rooms for expanded hotels when hotels change
  useEffect(() => {
    fetchRoomsForExpandedHotels(expandedHotels, hotels, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors));
  }, [hotels, expandedHotels]);

  // Create a separate function to fetch hotels so we can call it when needed
  // const fetchHotels = async () => {
  //   setLoading(prev => ({ ...prev, hotels: true }));
  //   try {
  //     const hotelsResponse = await hotelsAPI.getAll();
  //     setHotels(hotelsResponse.data.data.hotels);
  //     setErrors(prev => ({ ...prev, hotels: null }));
  //   } catch (error) {
  //     console.error('Error fetching hotels:', error);
  //     setErrors(prev => ({ ...prev, hotels: error.message || 'Failed to fetch hotels' }));
  //   } finally {
  //     setLoading(prev => ({ ...prev, hotels: false }));
  //   }
  // };

  // Function to fetch rooms for a specific hotel
  // const fetchRoomsForHotel = async (hotelId) => {
  //   // Prevent multiple simultaneous requests for the same hotel
  //   if (loading.rooms[hotelId]) {
  //     return;
  //   }
  //   
  //   setLoading(prev => ({
  //     ...prev,
  //     rooms: {
  //       ...prev.rooms,
  //       [hotelId]: true
  //     }
  //   }));
  //   
  //   try {
  //     const response = await roomsAPI.getByHotelId(hotelId);
  //     const hotelRooms = response.data?.data?.rooms || [];
  //     setRooms(prev => ({
  //       ...prev,
  //       [hotelId]: hotelRooms
  //     }));
  //     setErrors(prev => ({ ...prev, rooms: null }));
  //   } catch (error) {
  //     console.error(`Error fetching rooms for hotel ${hotelId}:`, error);
  //     setErrors(prev => ({ ...prev, rooms: error.message || `Failed to fetch rooms for hotel ${hotelId}` }));
  //   } finally {
  //     setLoading(prev => ({
  //       ...prev,
  //       rooms: {
  //         ...prev.rooms,
  //         [hotelId]: false
  //       }
  //     }));
  //   }
  // };

  // Get rooms for a specific hotel (from state)
  const getRoomsForHotel = (hotelId, rooms) => {
    return rooms[hotelId] || [];
  };

  // Hotel form validation - REPLACED WITH HELPER FUNCTION
  // const validateHotelForm = () => {
  //   return validateHotelForm(hotelFormData, editingHotel);
  // };

  // Room form validation
  const validateRoomForm = () => {
    return validateRoomForm(roomFormData);
  };

  // Search cities function
  // const searchCities = async (query) => {
  //   await searchCities(query, setCitySearchResults, setShowCitySearchResults, setLoading, setErrors);
  // };

  // Search hotels function
  // const searchHotels = async (cityId, countryCode) => {
  //   await searchHotels(cityId, countryCode, setSearchResults, setShowSearchResults, setShowCitySearchResults, setLoading, setErrors, TRAVEL_API_BASE);
  // };

  // Debounce function for search
  // const debounce = (func, delay) => {
  //   let timeoutId;
  //   return (...args) => {
  //     clearTimeout(timeoutId);
  //     timeoutId = setTimeout(() => func.apply(null, args), delay);
  //   };
  // };

  // Debounced search function
  const debouncedSearchCities = useCallback(debounce((query) => {
    if (query.trim()) {
      searchCities(query, setCitySearchResults, setShowCitySearchResults, setLoading, setErrors);
    } else {
      setCitySearchResults([]);
      setShowCitySearchResults(false);
    }
  }, 500), []);

  // Handle search input change
  // const handleSearchChange = (e) => {
  //   const value = e.target.value;
  //   setSearchTerm(value);
  
  //   debouncedSearchCities(value);
  // };

  // Handle search submit
  // const handleSearchSubmit = (e) => {
  //   e.preventDefault();
  // };

  // Select a city from search results
  // const selectCityFromSearch = (city) => {
  //   setSelectedCity(city);
  //   setSearchTerm(`${city.city_name}, ${city.country_name}`);
  //   setCitySearchResults([]);
  //   setShowCitySearchResults(false);
  
  //   searchHotels(city.city_code, city.country_code);
  // };

  // Select a hotel from search results
  // const selectHotelFromSearch = (hotel) => {
  //   setHotelFormData(prev => ({
  //     ...prev,
  //     name_en: hotel.HotelName || hotel.hotelName || '',
  //     accessibility_features: hotel.inclugoData?.accessibility_features || {
  //       wheelchair_accessible: false,
  //       elevator: false,
  //       visual_aids: false,
  //       hearing_aids: false,
  //       grab_bars: false,
  //       roll_in_shower: false,
  //       accessible_parking: false,
  //       service_animal_friendly: false
  //     },
  //     accessibility_features_description: hotel.inclugoData?.accessibility_features_description || '',
  //     featured: false,
  //     city: selectedCity?.city_name || '',
  //     country: selectedCity?.country_name || '',
  //     images_urls: [''], // Don't pre-fill images when adding a new hotel
  //     supplier_id: hotel.HotelCode || '', // Add supplier_id from HotelCode
  //     commission_percentage: hotel.CommissionPercentage || hotel.commissionPercentage || 10 // Add commission_percentage or default to 10
  //   }));
  
  //   setShowHotelForm(true);
  //   setShowSearchResults(false);
  //   setSearchTerm('');
  //   setSearchResults([]);
  // };

  // Handle image file change for hotels
  const handleHotelImageChange = (e) => {
    handleHotelImageChange(e, setHotelFormData);
  };

  // Handle image file change for rooms
  const handleRoomImageChange = (e) => {
    handleRoomImageChange(e, setRoomFormData);
  };

  // Hotel form handlers
  // Hotel form handlers

  // Handle hotel form submission with file upload
  const handleHotelFormSubmit = async (e) => {
    // Call the actual handler
    await handleHotelSubmitHelper(e, validateHotelForm, hotelFormData, editingHotel, setFormErrors, setErrors, () => fetchHotels(setLoading, setHotels, setErrors), () => resetHotelForm());
  };

  const handleEditHotel = (hotel) => {
    handleEditHotelHelper(hotel, setEditingHotel, setHotelFormData, setShowHotelForm, initializeHotelFormDataForEdit);
  };

  const handleDeleteHotel = async (id) => {
    await handleDeleteHotelHelper(id, setErrors, () => fetchHotels(setLoading, setHotels, setErrors));
  };

  // Function to initiate hotel deletion with confirmation
  const confirmDeleteHotel = (hotel) => {
    confirmDeleteHotelHelper(hotel, setHotelToDelete, setShowDeleteConfirm);
  };

  const resetHotelForm = () => {
    resetHotelFormHelper(setHotelFormData, setEditingHotel, setShowHotelForm, setErrors, setFormErrors);
  };

  // Room form handlers

  // Handle room form submission with file upload
  const handleRoomSubmit = async (e) => {
    await handleRoomSubmitHelper(e, validateRoomForm, roomFormData, setFormErrors, setErrors, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors), editingRoom, () => resetRoomForm());
  };

  const handleEditRoom = (room) => {
    handleEditRoomHelper(room, hotels, setEditingRoom, setSelectedHotelForRoomSearch, setTravelomatixRoomData, setTravelomatixRoomFormData, setShowTravelomatixRoomForm, initializeRoomFormDataForEdit);
  };

  const handleDeleteRoom = async (id, hotelId) => {
    await handleDeleteRoomHelper(id, hotelId, setErrors, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors));
  };

  const resetRoomForm = () => {
    resetRoomFormHelper(setRoomFormData, setEditingRoom, setShowRoomForm, setErrors, setFormErrors);
  };

  // Reset Travelomatix room form and clear editing state - REPLACED WITH HELPER FUNCTION
  // const resetTravelomatixRoomFormAndEdit = () => {
  //   resetTravelomatixRoomFormAndEditHelper(() => resetTravelomatixRoomFormHelper(setShowTravelomatixRoomForm, setTravelomatixRoomData, setRoomSearchError, setTravelomatixRoomFormData), setEditingRoom);
  // };

  // Search rooms function for a specific hotel
  const searchRooms = async (hotel) => {
    await searchRoomsHelper(hotel, setRoomSearchLoading, setRoomSearchError, setSelectedHotelForRoomSearch, setRoomSearchResults, setShowRoomSearchResults, TRAVEL_API_BASE, travelomatixRoomsAPI);
  };

  // Add room from Travelomatix to local database - REPLACED WITH HELPER FUNCTION
  // const addRoomFromTravelomatix = (roomData) => {
  //   addRoomFromTravelomatixHelper(roomData, selectedHotelForRoomSearch, setTravelomatixRoomData, setTravelomatixRoomFormData, setShowTravelomatixRoomForm, setShowRoomSearchResults, initializeRoomFormDataForAdd);
  // };

  // Handle Travelomatix room form input changes - REPLACED WITH HELPER FUNCTION
  // const handleTravelomatixRoomInputChange = (e) => {
  //   handleTravelomatixRoomInputChangeHelper(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors);
  // };

  // Validate image URLs
  const validImageUrls = travelomatixRoomFormData.images_urls.filter(url => url.trim() !== '');
  if (validImageUrls.some(url => !/^https?:\/\/.+\..+/.test(url))) {
    errors.images_urls = 'All image URLs must be valid URLs';
  }


  // Handle Travelomatix room form submission - REPLACED WITH HELPER FUNCTION
  // const handleTravelomatixRoomSubmit = async (e) => {
  //   await handleTravelomatixRoomSubmitHelper(e, () => validateTravelomatixRoomFormHelper(travelomatixRoomFormData), travelomatixRoomFormData, setTravelomatixFormErrors, formatRoomDataForSubmission, editingRoom, setRoomSearchError, fetchRoomsForHotel, setShowTravelomatixRoomForm, setTravelomatixRoomData, setEditingRoom, setTravelomatixRoomFormData);
  // };

  // Reset Travelomatix room form - REPLACED WITH HELPER FUNCTION
  // const resetTravelomatixRoomForm = () => {
  //   resetTravelomatixRoomFormHelper(setShowTravelomatixRoomForm, setTravelomatixRoomData, setRoomSearchError, setTravelomatixRoomFormData);
  // };

  // Handle room search form changes - REPLACED WITH HELPER FUNCTION
  const handleRoomSearchChange = (e) => {
    handleRoomSearchChangeHelper(e, setRoomSearchParams);
  };

  // Handle room search form submission - REPLACED WITH HELPER FUNCTION
  const handleRoomSearchSubmit = async (e) => {
    await handleRoomSearchSubmitHelper(e, selectedHotelForRoomSearch, roomSearchParams, setRoomSearchParams, setRoomSearchError, searchRooms, setShowRoomSearchModal);
  };

  // Reset room search form - REPLACED WITH HELPER FUNCTION
  const resetRoomSearchForm = () => {
    resetRoomSearchFormHelper(setRoomSearchParams, setRoomSearchError);
  };

  // Export functions





  // Skeleton loader component for hotel list



  return (
    <div className="hotels-page">
      <div className="page-header">
        <h1>Hotels & Rooms Management</h1>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => exportAllToExcel(hotels, setErrors, roomsAPI)}
            style={{ marginRight: '10px' }}
            disabled={loading.hotels}
          >
            {loading.hotels ? 'Loading...' : 'Export All to Excel'}
          </button>
          {showHotelForm && <button 
            className="btn btn-primary" 
            onClick={() => setShowHotelForm(!showHotelForm)}
          >
            Cancel
          </button>}
        </div>
      </div>

      {/* Search Form */}
      <div className="search-container">
        <form onSubmit={(e) => {
          e.preventDefault();
          if (selectedCity && checkInDate && checkOutDate) {
            searchHotels(selectedCity.city_code, selectedCity.country_code, setSearchResults, setShowSearchResults, setShowCitySearchResults, setLoading, setErrors, TRAVEL_API_BASE, checkInDate, checkOutDate);
          }
        }} className="search-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="search">Search Hotels by City or Country</label>
              <div className="search-input-container">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => handleSearchChangeHelper(e, setSearchTerm, debouncedSearchCities)}
                  placeholder="Enter city or country name"
                  className="search-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="checkInDate">Check-in Date</label>
              <DatePicker
                id="checkInDate"
                selected={checkInDate}
                onChange={(date) => setCheckInDate(date)}
                selectsStart
                startDate={checkInDate}
                endDate={checkOutDate}
                minDate={new Date()}
                placeholderText="Select check-in date"
                className="date-picker"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="checkOutDate">Check-out Date</label>
              <DatePicker
                id="checkOutDate"
                selected={checkOutDate}
                onChange={(date) => setCheckOutDate(date)}
                selectsEnd
                startDate={checkInDate}
                endDate={checkOutDate}
                minDate={checkInDate || new Date()}
                placeholderText="Select check-out date"
                className="date-picker"
              />
            </div>
            
            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="submit"
                className="btn btn-primary search-button"
                disabled={!selectedCity || !checkInDate || !checkOutDate || loading.search}
              >
                {loading.search ? 'Searching...' : 'Search Hotels'}
              </button>
            </div>
          </div>
        </form>
        
        {/* City Search Results Dropdown */}
        {showCitySearchResults && citySearchResults.length > 0 && (
          <div className="city-search-dropdown">
            <div className="dropdown-list">
              {citySearchResults.map((city, index) => (
                <div 
                  key={index} 
                  className="dropdown-item"
                  onClick={() => selectCityFromSearch(city, setSelectedCity, setSearchTerm, setCitySearchResults, setShowCitySearchResults)}
                >
                  <div className="city-info">
                    <div className="city-name">{city.city_name}</div>
                    <div className="country-name">{city.country_name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Search Results */}
        {showSearchResults && (
          <div className="search-results">
            <h3>Search Results</h3>
            {loading.search ? (
              <div className="loading-message search-loading">
                Searching for hotels in {selectedCity?.city_name}...
              </div>
            ) : searchResults.length > 0 ? (
              <div className="results-list">
                {searchResults
                  .filter(hotel => !hotel.inclugoData) // Only show hotels WITHOUT inclugoData
                  .map((hotel, index) => (
                    <div key={index} className="result-item">
                      <div className="result-info">
                        <h4>{hotel.HotelName || hotel.hotelName || 'Hotel Name'}</h4>
                        <p>{selectedCity?.city_name}, {selectedCity?.country_name}</p>
                        <div className="hotel-price">
                          {hotel.Price?.CurrencyCode} {hotel.Price?.RoomPrice}
                        </div>
                      </div>
                      <div className="result-actions">
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => selectHotelFromSearch(hotel, selectedCity, setHotelFormData, setShowHotelForm, setShowSearchResults, setSearchTerm, setSearchResults)}
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="loading-message search-loading">
                No hotels found for {selectedCity?.city_name}, {selectedCity?.country_name}.
              </div>
            )}
          </div>
        )}
        
        {errors.search && (
          <div className="error-message search-error">
            Error: {errors.search}
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.hotels && (
        <div className="error-message">
          Error loading hotels: {errors.hotels}
        </div>
      )}
      
      {errors.hotelForm && (
        <div className="error-message">
          Error saving hotel: {errors.hotelForm}
        </div>
      )}
      
      {errors.roomForm && (
        <div className="error-message">
          Error saving room: {errors.roomForm}
        </div>
      )}

      {showHotelForm && (
        <div className="hotel-form-container">
          <h2>{editingHotel ? 'Edit Hotel' : 'Add New Hotel'}</h2>
          <form onSubmit={handleHotelFormSubmit} className="hotel-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name_en">Hotel Name (English)*</label>
                <input
                  type="text"
                  id="name_en"
                  name="name_en"
                  value={hotelFormData.name_en}
                  onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  className={formErrors.hotel.name_en ? 'error' : ''}
                />
                {formErrors.hotel.name_en && (
                  <span className="error-text">{formErrors.hotel.name_en}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City*</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={hotelFormData.city}
                  onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  className={formErrors.hotel.city ? 'error' : ''}
                />
                {formErrors.hotel.city && (
                  <span className="error-text">{formErrors.hotel.city}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="country">Country*</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={hotelFormData.country}
                  onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  className={formErrors.hotel.country ? 'error' : ''}
                />
                {formErrors.hotel.country && (
                  <span className="error-text">{formErrors.hotel.country}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={hotelFormData.featured}
                    onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  />
                  Featured Hotel
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="supplier_id">Supplier ID</label>
                <input
                  type="text"
                  id="supplier_id"
                  name="supplier_id"
                  value={hotelFormData.supplier_id}
                  onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  placeholder="Enter supplier ID"
                  disabled={editingHotel || hotelFormData.supplier_id} // Disable when editing or when supplier_id is already set
                />
              </div>
              <div className="form-group">
                <label htmlFor="commission_percentage">Commission Percentage</label>
                <input
                  type="number"
                  id="commission_percentage"
                  name="commission_percentage"
                  value={hotelFormData.commission_percentage}
                  onChange={(e) => handleHotelInputChange(e, setHotelFormData, hotelFormData, setFormErrors, formErrors.hotel)}
                  placeholder="Enter commission percentage"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Accessibility Templates Selection */}
            <div className="form-group">
              <label>Accessibility Templates</label>
              {accessibilityLoading ? (
                <div className="loading-message">Loading accessibility templates...</div>
              ) : (
                <div className="accessibility-templates-selection">
                  {Object.keys(accessibilityTemplates).map(category => (
                    <div key={category} className="accessibility-category">
                      <h4 className="accessibility-category-title">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </h4>
                      <div className="checkbox-group">
                        {accessibilityTemplates[category].map(template => (
                          <label key={template.id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={hotelFormData.accessibility_template_ids.includes(template.id)}
                              onChange={(e) => {
                                const templateIds = [...hotelFormData.accessibility_template_ids];
                                if (e.target.checked) {
                                  templateIds.push(template.id);
                                } else {
                                  const index = templateIds.indexOf(template.id);
                                  if (index > -1) {
                                    templateIds.splice(index, 1);
                                  }
                                }
                                setHotelFormData(prev => ({
                                  ...prev,
                                  accessibility_template_ids: templateIds
                                }));
                              }}
                            />
                            <span className="template-name">
                              {template.name_en}
                              {template.feature_category && ` (${template.feature_category})`}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {Object.keys(accessibilityTemplates).length === 0 && (
                    <p className="no-templates-message">No accessibility features available.</p>
                  )}
                </div>
              )}
            </div>

            {/* Amenities Selection */}
            <div className="form-group">
              <label>Amenities</label>
              {amenitiesLoading ? (
                <div className="loading-message">Loading amenities...</div>
              ) : (
                <div className="amenities-selection">
                  <div className="checkbox-group">
                    {amenities.map(amenity => (
                      <label key={amenity.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={hotelFormData.amenities.includes(amenity.id)}
                          onChange={(e) => {
                            const amenityIds = [...hotelFormData.amenities];
                            if (e.target.checked) {
                              amenityIds.push(amenity.id);
                            } else {
                              const index = amenityIds.indexOf(amenity.id);
                              if (index > -1) {
                                amenityIds.splice(index, 1);
                              }
                            }
                            setHotelFormData(prev => ({
                              ...prev,
                              amenities: amenityIds
                            }));
                          }}
                        />
                        <span className="amenity-name">
                          {amenity.name_en}
                        </span>
                      </label>
                    ))}
                  </div>
                  {amenities.length === 0 && (
                    <p className="no-amenities-message">No amenities available. Please add amenities from the Amenities Management page.</p>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Image URLs</label>
              {editingHotel ? (
                <>
                  {hotelFormData.images_urls.map((url, index) => (
                    <div key={index} className="image-url-input">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          const newImageUrls = handleImageUrlChange(hotelFormData.images_urls, index, e.target.value);
                          setHotelFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        placeholder="https://example.com/image.jpg"
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-delete"
                        onClick={() => {
                          const newImageUrls = removeImageUrl(hotelFormData.images_urls, index);
                          setHotelFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        disabled={hotelFormData.images_urls.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const newImageUrls = addImageUrl(hotelFormData.images_urls);
                      setHotelFormData(prev => ({
                        ...prev,
                        images_urls: newImageUrls
                      }));
                    }}
                    style={{ marginTop: '10px' }}
                  >
                    + Add Another Image URL
                  </button>
                </>
              ) : (
                <>
                  {hotelFormData.images_urls.map((url, index) => (
                    <div key={index} className="image-url-input">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          const newImageUrls = handleImageUrlChange(hotelFormData.images_urls, index, e.target.value);
                          setHotelFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        placeholder="https://example.com/image.jpg"
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-delete"
                        onClick={() => {
                          const newImageUrls = removeImageUrl(hotelFormData.images_urls, index);
                          setHotelFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        disabled={hotelFormData.images_urls.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const newImageUrls = addImageUrl(hotelFormData.images_urls);
                      setHotelFormData(prev => ({
                        ...prev,
                        images_urls: newImageUrls
                      }));
                    }}
                    style={{ marginTop: '10px' }}
                  >
                    + Add Another Image URL
                  </button>
                  <p className="form-note">Note: Images can be added now or updated later.</p>
                </>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetHotelForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingHotel ? 'Update Hotel' : 'Add Hotel'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="hotels-list">
        <div className="table-header">
          <h2>Hotels List</h2>
          <div className="table-actions">
            <button className="btn btn-secondary btn-small" onClick={() => exportHotelsToExcel(hotels)}>
              Export Hotels
            </button>
          </div>
        </div>
        
        {loading.hotels ? (
          <>
            {[...Array(3)].map((_, index) => (
              <HotelSkeleton key={index} />
            ))}
          </>
        ) : (
          <>
            {paginatedHotels && paginatedHotels.length > 0 ? (
              paginatedHotels.map((hotel, index) => {
                const hotelRooms = getRoomsForHotel(hotel.id, rooms);
                console.log('Hotel Rooms:', hotelRooms);
                const isLoadingRooms = loading.rooms && loading.rooms[hotel.id] || false;
                const isExpanded = expandedHotels.has(hotel.id);
                
                return (
                  <div key={hotel.id} className="hotel-section">
                    <div 
                      className={`hotel-header ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleHotelDetails(hotel.id, expandedHotels, setExpandedHotels, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors))}
                    >
                      <div className="hotel-info">
                        <div>
                          <h3>{hotel.name_en || ''}</h3>
                          <div className="hotel-location">{hotel.city || ''}, {hotel.country || ''}</div>
                        </div>
                      </div>
                      <div className="hotel-actions">
                        <button 
                          className="btn btn-small btn-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleHotelDetails(hotel.id, expandedHotels, setExpandedHotels, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors));
                          }}
                        >
                          {isExpanded ? 'Hide Details' : 'Show Details'}
                        </button>

                        <button 
                          className="btn btn-small btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditHotel(hotel, setEditingHotel, setHotelFormData, setShowHotelForm, initializeHotelFormDataForEdit);
                          }}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-small btn-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteHotel(hotel, setHotelToDelete, setShowDeleteConfirm);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="hotel-details">
                        <div className="hotel-description">
                          <p className="arabic-text">{hotel.name_ar ? `${hotel.name_ar} (Arabic Name)` : ''}</p>
                        </div>
                        <div className="hotel-meta">
                          <div>Supplier ID: {hotel.supplier_id || 'N/A'}</div>
                          <div>Commission: {hotel.commission_percentage || 0}%</div>
                          <div>Featured: {hotel.featured ? 'Yes' : 'No'}</div>
                        </div>
                        
                        <div className="rooms-section">
                          <div className="table-header">
                            <h4>Rooms</h4>
                            <div className="table-actions">
                              <button 
                                className="btn btn-secondary btn-small"
                                onClick={() => exportRoomsToExcel(hotel.id, hotel.name_en, setErrors)}
                                disabled={isLoadingRooms}
                                style={{ marginRight: '10px' }}
                              >
                                {isLoadingRooms ? 'Loading...' : 'Export Rooms'}
                              </button>
                              <button 
                                className="btn btn-primary btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  searchRooms(hotel);
                                }}
                                disabled={roomSearchLoading}
                              >
                                {roomSearchLoading && selectedHotelForRoomSearch?.id === hotel.id ? 'Searching...' : 'Search Rooms from Travelomatix'}
                              </button>
                            </div>
                          </div>
                          
                          {isLoadingRooms ? (
                            <div className="loading-message">Loading rooms...</div>
                          ) : hotelRooms && hotelRooms.length > 0 ? (
                            <table className="rooms-table">
                              <thead>
                                <tr>
                                  <th>Room Type</th>
                                  <th>Name</th>
                                  <th>Capacity</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hotelRooms.map((room) => (
                                  <tr key={room.id}>
                                    <td>{room.room_type}</td>
                                    <td>{room.name_en}</td>
                                    <td>{room.capacity} Guest{room.capacity > 1 ? 's' : ''}</td>
                                    <td>
                                      <button 
                                        className="btn btn-small btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditRoom(room);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        className="btn btn-small btn-delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteRoom(room.id, hotel.id);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="no-rooms">
                              <p>No rooms found for this hotel.</p>
                              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>

                                <button 
                                  className="btn btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    searchRooms(hotel);
                                  }}
                                  disabled={roomSearchLoading}
                                >
                                  {roomSearchLoading && selectedHotelForRoomSearch?.id === hotel.id ? 'Searching...' : 'Search Rooms from Travelomatix'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-rooms">
                <p>No hotels found.</p>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(hotelPage - 1, setHotelPage)}
                  disabled={hotelPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {hotelPage} of {totalPages}
                </span>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(hotelPage + 1, setHotelPage)}
                  disabled={hotelPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the hotel "{hotelToDelete?.name_en}"? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => handleCancelDelete(setShowDeleteConfirm, setHotelToDelete)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => handleConfirmDelete(hotelToDelete, setHotelToDelete, setShowDeleteConfirm, setErrors, () => fetchHotels(setLoading, setHotels, setErrors))}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Room Search Results */}
      {showRoomSearchResults && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Travelomatix Room Results</h3>
            </div>
            <div className="modal-body">
              {roomSearchLoading ? (
                <div className="loading-message">Searching for rooms...</div>
              ) : roomSearchError ? (
                <div className="error-message">{roomSearchError}</div>
              ) : (
                <div className="room-search-results">
                  <h4>Available Rooms</h4>
                  {roomSearchResults.length > 0 ? (
                    roomSearchResults.map((room, index) => (
                      <div key={index} className="room-result-item">
                        <div className="room-result-info">
                          <h4>{room.RoomTypeName || 'Room'}</h4>
                          <p>Room Type: {room.RoomType || 'N/A'}</p>
                          <p>Meal Type: {room.MealTypeName || 'N/A'}</p>
                          <div className="room-price">
                            {room.Price?.Currency} {room.Price?.RoomPrice}
                          </div>
                        </div>
                        <div className="room-actions">
                          <button
                            className="btn btn-primary"
                            onClick={() => addRoomFromTravelomatix(room, selectedHotelForRoomSearch, setTravelomatixRoomData, setTravelomatixRoomFormData, setShowTravelomatixRoomForm, setShowRoomSearchResults, initializeRoomFormDataForAdd)}
                          >
                            Add to Database
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No rooms found for the selected criteria.</p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowRoomSearchResults(false);
                  setRoomSearchResults([]);
                  setRoomSearchError(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Travelomatix Room Form */}
      {showTravelomatixRoomForm && travelomatixRoomData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Room from Travelomatix</h3>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => handleTravelomatixRoomSubmit(e, () => validateTravelomatixRoomForm(travelomatixRoomFormData), travelomatixRoomFormData, setTravelomatixFormErrors, formatRoomDataForSubmission, editingRoom, setRoomSearchError, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors), setShowTravelomatixRoomForm, setTravelomatixRoomData, setEditingRoom, setTravelomatixRoomFormData)} className="room-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_hotel_id">Hotel*</label>
                    <input
                      type="text"
                      id="travelomatix_hotel_id"
                      value={selectedHotelForRoomSearch?.name_en || ''}
                      disabled
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="travelomatix_room_type">Room Type*</label>
                    <input
                      type="text"
                      id="travelomatix_room_type"
                      name="room_type"
                      value={travelomatixRoomFormData.room_type}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_name_en">Room Name (English)*</label>
                    <input
                      type="text"
                      id="travelomatix_name_en"
                      name="name_en"
                      value={travelomatixRoomFormData.name_en}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_supplier_id">Supplier ID</label>
                    <input
                      type="text"
                      id="travelomatix_supplier_id"
                      name="supplier_id"
                      value={travelomatixRoomFormData.supplier_id}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled
                      className="form-control"
                    />
                  </div>
                </div>

                {/* Accessibility Templates Selection */}
                <div className="form-group">
                  <label>Accessibility Templates</label>
                  {accessibilityLoading ? (
                    <div className="loading-message">Loading accessibility templates...</div>
                  ) : (
                    <div className="accessibility-templates-selection">
                      {Object.keys(accessibilityTemplates).map(category => (
                        <div key={category} className="accessibility-category">
                          <h4 className="accessibility-category-title">
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </h4>
                          <div className="checkbox-group">
                            {accessibilityTemplates[category].map(template => (
                              <label key={template.id} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={travelomatixRoomFormData.accessibility_template_ids.includes(template.id)}
                                  onChange={(e) => {
                                    const templateIds = [...travelomatixRoomFormData.accessibility_template_ids];
                                    if (e.target.checked) {
                                      templateIds.push(template.id);
                                    } else {
                                      const index = templateIds.indexOf(template.id);
                                      if (index > -1) {
                                        templateIds.splice(index, 1);
                                      }
                                    }
                                    setTravelomatixRoomFormData(prev => ({
                                      ...prev,
                                      accessibility_template_ids: templateIds
                                    }));
                                  }}
                                />
                                <span className="template-name">
                                  {template.name_en}
                                  {template.feature_category && ` (${template.feature_category})`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      {Object.keys(accessibilityTemplates).length === 0 && (
                        <p className="no-templates-message">No accessibility features available.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Image URLs</label>
                  {travelomatixRoomFormData.images_urls.map((url, index) => (
                    <div key={index} className="image-url-input">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          const newImageUrls = handleImageUrlChange(travelomatixRoomFormData.images_urls, index, e.target.value);
                          setTravelomatixRoomFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        placeholder="https://example.com/image.jpg"
                        className="form-control"
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-delete"
                        onClick={() => {
                          const newImageUrls = removeImageUrl(travelomatixRoomFormData.images_urls, index);
                          setTravelomatixRoomFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        disabled={travelomatixRoomFormData.images_urls.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const newImageUrls = addImageUrl(travelomatixRoomFormData.images_urls);
                      setTravelomatixRoomFormData(prev => ({
                        ...prev,
                        images_urls: newImageUrls
                      }));
                    }}
                    style={{ marginTop: '10px' }}
                  >
                    + Add Another Image URL
                  </button>
                  <p className="form-note">Note: Add image URLs for this room.</p>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => resetTravelomatixRoomForm(setShowTravelomatixRoomForm, setTravelomatixRoomData, setRoomSearchError, setTravelomatixRoomFormData)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Room
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Travelomatix Room Form for Editing/Adding */}
      {showTravelomatixRoomForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingRoom ? 'Edit Room' : 'Add Room from Travelomatix'}</h3>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => handleTravelomatixRoomSubmit(e, () => validateTravelomatixRoomForm(travelomatixRoomFormData), travelomatixRoomFormData, setTravelomatixFormErrors, formatRoomDataForSubmission, editingRoom, setRoomSearchError, (hotelId) => fetchRoomsForHotel(hotelId, loading, setLoading, setRooms, setErrors), setShowTravelomatixRoomForm, setTravelomatixRoomData, setEditingRoom, setTravelomatixRoomFormData)} className="room-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_hotel_id">Hotel*</label>
                    <input
                      type="text"
                      id="travelomatix_hotel_id"
                      value={selectedHotelForRoomSearch?.name_en || ''}
                      disabled
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="travelomatix_room_type">Room Type*</label>
                    <input
                      type="text"
                      id="travelomatix_room_type"
                      name="room_type"
                      value={travelomatixRoomFormData.room_type}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled={!!editingRoom}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_name_en">Room Name (English)*</label>
                    <input
                      type="text"
                      id="travelomatix_name_en"
                      name="name_en"
                      value={travelomatixRoomFormData.name_en}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled={!!editingRoom}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="travelomatix_supplier_id">Supplier ID</label>
                    <input
                      type="text"
                      id="travelomatix_supplier_id"
                      name="supplier_id"
                      value={travelomatixRoomFormData.supplier_id}
                      onChange={(e) => handleTravelomatixRoomInputChange(e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors)}
                      disabled={!!editingRoom}
                      className="form-control"
                    />
                  </div>
                </div>

                {/* Accessibility Templates Selection */}
                <div className="form-group">
                  <label>Accessibility Templates</label>
                  {accessibilityLoading ? (
                    <div className="loading-message">Loading accessibility templates...</div>
                  ) : (
                    <div className="accessibility-templates-selection">
                      {Object.keys(accessibilityTemplates).map(category => (
                        <div key={category} className="accessibility-category">
                          <h4 className="accessibility-category-title">
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </h4>
                          <div className="checkbox-group">
                            {accessibilityTemplates[category].map(template => (
                              <label key={template.id} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={travelomatixRoomFormData.accessibility_template_ids.includes(template.id)}
                                  onChange={(e) => {
                                    const templateIds = [...travelomatixRoomFormData.accessibility_template_ids];
                                    if (e.target.checked) {
                                      templateIds.push(template.id);
                                    } else {
                                      const index = templateIds.indexOf(template.id);
                                      if (index > -1) {
                                        templateIds.splice(index, 1);
                                      }
                                    }
                                    setTravelomatixRoomFormData(prev => ({
                                      ...prev,
                                      accessibility_template_ids: templateIds
                                    }));
                                  }}
                                />
                                <span className="template-name">
                                  {template.name_en}
                                  {template.feature_category && ` (${template.feature_category})`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      {Object.keys(accessibilityTemplates).length === 0 && (
                        <p className="no-templates-message">No accessibility features available.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Image URLs</label>
                  {travelomatixRoomFormData.images_urls.map((url, index) => (
                    <div key={index} className="image-url-input">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          const newImageUrls = handleImageUrlChange(travelomatixRoomFormData.images_urls, index, e.target.value);
                          setTravelomatixRoomFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        placeholder="https://example.com/image.jpg"
                        className="form-control"
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-delete"
                        onClick={() => {
                          const newImageUrls = removeImageUrl(travelomatixRoomFormData.images_urls, index);
                          setTravelomatixRoomFormData(prev => ({
                            ...prev,
                            images_urls: newImageUrls
                          }));
                        }}
                        disabled={travelomatixRoomFormData.images_urls.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const newImageUrls = addImageUrl(travelomatixRoomFormData.images_urls);
                      setTravelomatixRoomFormData(prev => ({
                        ...prev,
                        images_urls: newImageUrls
                      }));
                    }}
                    style={{ marginTop: '10px' }}
                  >
                    + Add Another Image URL
                  </button>
                  <p className="form-note">Note: Add image URLs for this room.</p>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => resetTravelomatixRoomFormAndEdit(() => resetTravelomatixRoomForm(setShowTravelomatixRoomForm, setTravelomatixRoomData, setRoomSearchError, setTravelomatixRoomFormData), setEditingRoom)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingRoom ? 'Update Room' : 'Add Room'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default Hotels;